import {
  Avatar,
  Button,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Link,
  Modal,
  ModalBody,
  ModalContent,
} from '@heroui/react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { OpioidChart } from './OpioidChart';

export function OpioidResult() {
  const { state } = useLocation();

  const [modal, toggleModal] = useState(false);

  const result = state?.result ?? 0;
  const average = state?.average ?? 0;
  const education = state?.education ?? null;

  const patientScore = Math.round(result * 100);
  const peerAvg = average * 100;
  const scoreColor = patientScore > peerAvg ? 'red' : 'green';
  const altText = `A bar chart compares your risk of opioid use to your peers. Your score of ${patientScore}%
    is represented by the ${scoreColor} bar. The average score of your peers is ${peerAvg}% as represented by
    the orange bar.`;

  const eduHTML = useMemo(() => {
    if (!education) {
      return <div />;
    }

    let html = '';

    education.forEach((component, i) => {
      html += component.html;

      if (i !== education.length - 1) {
        html += '<hr>';
      }
    });

    return (
      <div
        className="ed-v3"
        data-test="rec-edu-html"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }, [education]);

  const getChartData = () => {
    const data = [
      { group: 'me', answer: patientScore, fill: scoreColor },
      { group: 'peers', answer: peerAvg, fill: '#FF8C26' },
    ];
    return data;
  };

  return (
    <>
      <Modal
        isOpen={modal}
        onClose={() => toggleModal(false)}
        scrollBehavior="inside"
        size="5xl"
      >
        <ModalContent>
          <ModalBody>{eduHTML}</ModalBody>
        </ModalContent>
      </Modal>
      <div className="flex flex-col min-h-[100dvh]">
        <main className="flex justify-center">
          <div className="flex flex-col w-full max-w-[800px] text-left p-12 space-y-4">
            <Card>
              <CardHeader className="gap-4">
                <Avatar
                  size="md"
                  src="/icons/med-line.png"
                  style={{ backgroundColor: '#9B51E0' }}
                />
                <h3 className="font-semibold text-lg">
                  Risk for opioid use after discharge
                </h3>
              </CardHeader>
              <Divider />
              <CardBody className="pl-8">
                <div className="mx-auto w-full" role="figure">
                  <OpioidChart
                    altText={altText}
                    data={getChartData()}
                    yLimits={[0, 100]}
                  />
                </div>
                <div className="space-y-4">
                  <p className="text-sm">
                    You are at {patientScore}% risk of opioid use after
                    discharge. The average risk of your peer group is {peerAvg}
                    %.
                  </p>
                  <Link
                    href="#"
                    className="text-sm"
                    onPress={() => toggleModal(true)}
                  >
                    Learn more about your score and opioid risk
                  </Link>
                  <p className="text-sm">
                    Lifestyle modifications can change this score. Check how
                    your score changes when you answer in a different way!
                  </p>
                </div>
              </CardBody>
            </Card>
          </div>
        </main>
        <footer className="flex justify-center p-4 space-x-4">
          <Button
            as={Link}
            className="w-1/2 md:w-1/4"
            color="primary"
            href="/"
            size="lg"
          >
            Ok, got it
          </Button>
        </footer>
      </div>
    </>
  );
}
